import {
  MutationCache,
  MutationOptions,
  QueryCache,
  QueryClient,
  QueryKey,
  QueryOptions,
  UseQueryOptions as TanstackUseQueryOptions,
} from '@tanstack/react-query';

import { isSilentCancel } from '@/portainer/silent-cancel-error';
import { notifyError } from '@/portainer/services/notifications';

/**
 * @deprecated for `useQuery` ONLY. Use `withGlobalError`.
 *
 * `onError` and other callbacks are not supported on `useQuery` in react-query v5
 *
 * Using `withError` is fine for mutations (`useMutation`)
 *
 * see https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
 */
export function withError(fallbackMessage?: string, title = 'Failure') {
  return {
    onError(error: unknown) {
      notifyError(title, error as Error, fallbackMessage);
    },
  };
}

export function withGlobalError(fallbackMessage?: string, title = 'Failure') {
  return {
    meta: {
      error: { message: fallbackMessage, title },
    },
  };
}

type OptionalReadonly<T> = T | Readonly<T>;

export function withInvalidate(
  queryClient: QueryClient,
  queryKeysToInvalidate: Array<OptionalReadonly<Array<unknown>>>,
  // skipRefresh will set the mutation state to success without waiting for the invalidated queries to refresh
  // see the following for info: https://tkdodo.eu/blog/mastering-mutations-in-react-query#awaited-promises
  { skipRefresh }: { skipRefresh?: boolean } = {}
) {
  return {
    onSuccess() {
      const promise = Promise.all(
        queryKeysToInvalidate.map((keys) => queryClient.invalidateQueries(keys))
      );
      return skipRefresh
        ? undefined // don't wait for queries to refresh before setting state to success
        : promise; // stay loading until all queries are refreshed
    },
  };
}

export function mutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(...options: MutationOptions<TData, TError, TVariables, TContext>[]) {
  return mergeOptions(options);
}

export function queryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(...options: QueryOptions<TQueryFnData, TError, TData, TQueryKey>[]) {
  return mergeOptions(options);
}

/**
 * Pick the options we generally override
 *
 * This type is exported to avoid redefining types everywhere for common options
 * 'enabled' | 'select' | 'refetchInterval' | 'retry' | 'meta'
 *
 * @Example
 *
 * ```ts
 * export function useMyCustomHook( myProps: MyProps, options: UseQueryOptions) {
 *  return useQuery( ...keys...,
 *  () => getMyThing(myProps),
 *  {
 *    // aggregate the options provided by the caller
 *    ...options,
 *    // override here the options you want to define, make sure to include the caller-provided when they conflict
 *    enabled: xx && options.enabled,
 *  })
 * }
 * ```
 */
export type UseQueryOptions<T> = Pick<
  TanstackUseQueryOptions<T>,
  'enabled' | 'select' | 'refetchInterval' | 'retry' | 'meta'
>;

function mergeOptions<T>(options: T[]) {
  return options.reduce(
    (acc, option) => ({
      ...acc,
      ...option,
    }),
    {} as T
  );
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        networkMode: 'offlineFirst',
        staleTime: 20,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, variable, context, mutation) => {
        handleError(error, mutation.meta?.error);
      },
    }),
    queryCache: new QueryCache({
      onError: (error, mutation) => {
        handleError(error, mutation.meta?.error);
      },
    }),
  });
}

function handleError(error: unknown, errorMeta?: unknown) {
  if (isSilentCancel(error)) {
    return;
  }
  if (errorMeta && typeof errorMeta === 'object') {
    const { title = 'Failure', message } = errorMeta as {
      title?: string;
      message?: string;
    };

    notifyError(title, error as Error, message);
  }
}

export const queryClient = createQueryClient();
